<template>
  <v-row>
    <v-col cols="12">
      <v-card class="inv-card" flat>
        <!-- ================= HEADER BAND ================= -->
        <div class="inv-header pl-base-bg">
          <div class="inv-header-left">
            <div class="inv-header-icon">
              <v-icon color="#fff" size="22">mdi-file-document-outline</v-icon>
            </div>
            <div>
              <div class="inv-title">Invoice #{{ invoice?.invoiceNumber }}</div>
              <div class="inv-subtitle">
                Created on {{ formatDate(invoice?.invoiceDate) }}
              </div>
            </div>
          </div>

          <div class="inv-header-right">
            <v-chip
              :color="statusColor(invoice?.status)"
              dark
              small
              class="text-uppercase font-weight-bold inv-status-chip"
            >
              {{ invoice?.status }}
            </v-chip>

            <div class="inv-actions">
              <v-tooltip v-if="document" bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    class="inv-action-btn inv-action-btn--view"
                    :href="document.fileUrl"
                    target="_blank"
                  >
                    <v-icon small color="#fff">mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>View document</span>
              </v-tooltip>

              <v-tooltip v-if="document" bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    class="inv-action-btn inv-action-btn--download"
                    @click="downloadFile(document.fileKey)"
                  >
                    <v-icon small color="#fff">mdi-download</v-icon>
                  </v-btn>
                </template>
                <span>Download</span>
              </v-tooltip>

              <v-tooltip v-if="document" bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    class="inv-action-btn inv-action-btn--whatsapp"
                    @click="shareToWhatsApp"
                  >
                    <v-icon small color="#fff">mdi-whatsapp</v-icon>
                  </v-btn>
                </template>
                <span>Share on WhatsApp</span>
              </v-tooltip>

              <v-btn
                v-if="!isViewOnly"
                small
                class="inv-credit-note-btn"
                :disabled="!canIssueCreditNote"
                :to="`/credit-notes/create?invoiceId=${invoice?._id}`"
              >
                Issue Credit Note
              </v-btn>

              <v-btn
                v-if="!isViewOnly"
                small
                class="inv-pay-btn"
                @click="openPaymentDialog"
                :disabled="invoice?.status === 'PAID'"
              >
                Mark as Paid
              </v-btn>
            </div>
          </div>
        </div>

        <div class="pa-6">
          <!-- ================= SELLER / CUSTOMER ================= -->
          <v-row>
            <!-- SELLER -->
            <v-col cols="12" md="6">
              <div class="inv-party-card">
                <div class="inv-party-label">From</div>
                <div class="inv-party-name">
                  {{ invoice?.seller?.businessName }}
                </div>
                <div class="inv-party-line">{{ invoice?.seller?.address }}</div>
                <div class="inv-party-line" v-if="invoice?.seller?.gstin">
                  GSTIN: {{ invoice?.seller?.gstin }}
                </div>
                <div class="inv-party-line" v-if="invoice?.seller?.phone">
                  <v-icon size="14" class="mr-1">mdi-phone-outline</v-icon>
                  {{ invoice?.seller?.phone }}
                </div>
                <div class="inv-party-line" v-if="invoice?.seller?.email">
                  <v-icon size="14" class="mr-1">mdi-email-outline</v-icon>
                  {{ invoice?.seller?.email }}
                </div>
              </div>
            </v-col>

            <!-- CUSTOMER -->
            <v-col cols="12" md="6">
              <div class="inv-party-card inv-party-card--customer">
                <div class="inv-party-label">Bill To</div>
                <div class="inv-party-name">{{ invoice?.customer?.name }}</div>
                <div class="inv-party-line">
                  {{ invoice?.customer?.address }}
                </div>
                <div class="inv-party-line" v-if="invoice?.customer?.gstin">
                  GSTIN: {{ invoice?.customer?.gstin }}
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- ================= META ================= -->
          <div class="inv-meta-strip">
            <div class="inv-meta-item">
              <div class="inv-meta-label">Invoice Date</div>
              <div class="inv-meta-value">
                {{ formatDate(invoice?.invoiceDate) }}
              </div>
            </div>

            <div class="inv-meta-divider" />

            <div class="inv-meta-item">
              <div class="inv-meta-label">Due Date</div>
              <div class="inv-meta-value">
                {{ formatDate(invoice?.dueDate) }}
              </div>
            </div>

            <div class="inv-meta-divider" />

            <div class="inv-meta-item">
              <div class="inv-meta-label">Place of Supply</div>
              <div class="inv-meta-value">
                {{ invoice?.placeOfSupply || "-" }}
              </div>
            </div>
          </div>

          <!-- ================= ITEMS ================= -->
          <div class="inv-items-wrap">
            <div class="table-scroll">
              <v-simple-table dense class="inv-items-table">
                <thead>
                  <tr>
                    <th class="text-left">Product Name</th>
                    <th class="text-right">HSN / SAC</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Rate</th>
                    <th class="text-right">Tax %</th>
                    <th class="text-right">Amount</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(item, i) in invoice?.items" :key="i">
                    <td>{{ item.product }}</td>
                    <td class="text-right">{{ item.hsn }}</td>
                    <td class="text-right">{{ item.quantity }}</td>
                    <td class="text-right">
                      ₹ {{ formatCurrency(item.price) }}
                    </td>
                    <td class="text-right">{{ item.taxPercent }}%</td>
                    <td class="text-right font-weight-medium">
                      ₹ {{ formatCurrency(item.amount) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </div>

          <!-- ================= TOTAL ================= -->
          <v-row justify="end" class="totals">
            <v-col cols="12" md="4">
              <div class="inv-totals-card">
                <v-row dense>
                  <v-col class="grey--text py-1">Subtotal</v-col>
                  <v-col class="text-right py-1">
                    ₹ {{ formatCurrency(invoice?.subtotal) }}
                  </v-col>
                </v-row>

                <template v-if="hasTaxSplit">
                  <v-row v-if="invoice.isInterState" dense>
                    <v-col class="grey--text py-1">IGST</v-col>
                    <v-col class="text-right py-1">
                      ₹ {{ formatCurrency(invoice?.igst) }}
                    </v-col>
                  </v-row>
                  <template v-else>
                    <v-row dense>
                      <v-col class="grey--text py-1">CGST</v-col>
                      <v-col class="text-right py-1">
                        ₹ {{ formatCurrency(invoice?.cgst) }}
                      </v-col>
                    </v-row>
                    <v-row dense>
                      <v-col class="grey--text py-1">SGST</v-col>
                      <v-col class="text-right py-1">
                        ₹ {{ formatCurrency(invoice?.sgst) }}
                      </v-col>
                    </v-row>
                  </template>
                </template>
                <v-row v-else dense>
                  <v-col class="grey--text py-1">GST</v-col>
                  <v-col class="text-right py-1">
                    ₹ {{ formatCurrency(invoice?.gst) }}
                  </v-col>
                </v-row>

                <template v-if="invoice?.status === 'PARTIALLY_PAID'">
                  <v-row dense class="mt-1">
                    <v-col class="grey--text py-1">Amount Paid</v-col>
                    <v-col class="text-right py-1 green--text font-weight-medium">
                      ₹ {{ formatCurrency(invoice?.amountPaid) }}
                    </v-col>
                  </v-row>
                </template>

                <v-row v-if="invoice?.creditedAmount > 0" dense>
                  <v-col class="grey--text py-1">Credited</v-col>
                  <v-col class="text-right py-1 blue--text font-weight-medium">
                    ₹ {{ formatCurrency(invoice?.creditedAmount) }}
                  </v-col>
                </v-row>

                <!-- Already fully paid, then a credit note reduced the
                     invoice's value afterwards — the customer paid more
                     than the invoice is now worth, so this is a refund
                     owed BY the business, not an amount still owed by
                     the customer. Distinct from "Balance Due" below,
                     which only applies while payment is still pending. -->
                <v-row v-if="invoice?.status === 'PAID' && invoice?.creditedAmount > 0" dense>
                  <v-col class="grey--text py-1">Refund Due</v-col>
                  <v-col class="text-right py-1 orange--text text--darken-2 font-weight-medium">
                    ₹ {{ formatCurrency(invoice.creditedAmount) }}
                  </v-col>
                </v-row>

                <v-row v-if="invoice?.status === 'PARTIALLY_PAID'" dense>
                  <v-col class="grey--text py-1">Balance Due</v-col>
                  <v-col class="text-right py-1 red--text font-weight-medium">
                    ₹ {{ formatCurrency(remainingBalance) }}
                  </v-col>
                </v-row>

                <v-divider class="my-2" />

                <v-row dense class="text-h6 font-weight-bold">
                  <v-col>Total</v-col>
                  <v-col class="text-right inv-total-value">
                    ₹ {{ formatCurrency(invoice?.totalAmount) }}
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>

          <!-- ================= PAYMENT HISTORY ================= -->
          <v-row v-if="invoice?.payments?.length" class="mt-2">
            <v-col cols="12">
              <div class="inv-section-label">Payment History</div>

              <div class="inv-items-wrap">
                <div class="table-scroll">
                  <v-simple-table dense class="inv-items-table">
                    <thead>
                      <tr>
                        <th class="text-left">Date</th>
                        <th class="text-left">Method</th>
                        <th class="text-right">Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr v-for="(payment, i) in invoice.payments" :key="i">
                        <td>{{ formatDate(payment.paidAt) }}</td>
                        <td>{{ formatPaymentMethod(payment.paymentMethod) }}</td>
                        <td class="text-right font-weight-medium">
                          ₹ {{ formatCurrency(payment.amount) }}
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- ================= NOTE ================= -->
          <v-row v-if="invoice?.note" class="mt-4">
            <v-col>
              <v-alert type="info" outlined dense>
                {{ invoice.note }}
              </v-alert>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </v-col>

    <!-- GLOBAL PAYMENT DIALOG -->
    <global-dialog
      v-model="paymentDialog"
      :title="dialogTitle"
      :buttons="dialogButtons"
    >
      <v-row dense>
        <v-col cols="12" class="mt-3">
          <v-alert type="info" dense text>
            You can enter a partial amount to record a partial payment. The remaining balance will stay as due.
          </v-alert>
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="paymentAmount"
            label="Amount"
            type="number"
            outlined
            dense
            :hint="`Balance due: ₹ ${formatCurrency(remainingBalance)}`"
            persistent-hint
            :error-messages="
              paymentAmount && !validPaymentAmount
                ? `Amount must be between 1 and ₹ ${formatCurrency(remainingBalance)}`
                : ''
            "
          />

          <v-select
            v-model="paymentMethod"
            :items="paymentMethods"
            label="Payment Method"
            outlined
            dense
            required
            class="mt-4"
          />
        </v-col>
      </v-row>
    </global-dialog>
  </v-row>
</template>

<script>
import GlobalDialog from "@/components/common/global-dialog.vue";
import { triggerBlobDownload } from "@/utils/download-blob";
export default {
  name: "ViewInvoice",
  components: {
    GlobalDialog,
  },

  data() {
    return {
      invoice: null,
      document: null,
      paymentMethod: "",
      paymentAmount: 0,
      paymentDialog: false,

      paymentMethods: [
        "CASH",
        "BANK_TRANSFER",
        "UPI",
        "DEBIT_CARD",
        "CREDIT_CARD",
        "CHEQUE",
        "NET_BANKING",
        "WALLET",
        "OTHER",
      ],
    };
  },

  async created() {
    const invoiceId = this.$route.params.id;

    const res = await this.$store.dispatch(
      "invoices/getInvoiceById",
      invoiceId,
    );

    this.invoice = res.invoice;
    this.document = res.document;
  },

  computed: {
    dialogTitle() {
      return this.invoice
        ? `Confirm Payment - ${this.invoice.invoiceNumber}`
        : "Confirm Payment";
    },

    dialogButtons() {
      return [
        {
          text: "Cancel",
          color: "secondary",
          action: this.closePaymentDialog,
        },
        {
          text: "Confirm",
          color: "primary",
          disabled: !this.paymentMethod || !this.validPaymentAmount,
          action: this.confirmPayment,
        },
      ];
    },

    remainingBalance() {
      return (
        (this.invoice?.totalAmount || 0) -
        (this.invoice?.amountPaid || 0) -
        (this.invoice?.creditedAmount || 0)
      );
    },

    canIssueCreditNote() {
      if (!this.invoice) return false;
      if (this.invoice.status === "CANCELLED") return false;
      const remaining =
        (this.invoice.totalAmount || 0) - (this.invoice.creditedAmount || 0);
      return remaining > 0.01;
    },

    // Older invoices, saved before CGST/SGST/IGST fields existed, only have
    // the flat `gst` total — Mongoose backfills cgst/sgst/igst to 0 for them
    // on read, same as a genuinely zero-tax new invoice, so cgst+sgst+igst
    // alone can't tell the two apart. A legacy invoice is only distinguishable
    // by having tax (`gst > 0`) that never got split; fall back to the flat
    // display only in that case — a real zero-tax invoice still shows its
    // (zero-valued) CGST/SGST or IGST breakdown correctly.
    hasTaxSplit() {
      const inv = this.invoice;
      if (!inv) return false;
      const split = (inv.cgst || 0) + (inv.sgst || 0) + (inv.igst || 0);
      return split > 0 || !(inv.gst > 0);
    },

    validPaymentAmount() {
      const v = parseFloat(this.paymentAmount);
      return !isNaN(v) && v > 0 && v <= this.remainingBalance;
    },
  },

  methods: {
    formatDate(date) {
      if (!date) return "-";

      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    formatCurrency(value) {
      if (value === null || value === undefined || value === "") return "0.00";

      return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },

    formatPaymentMethod(method) {
      if (!method) return "-";
      return method
        .toLowerCase()
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    },

    openPaymentDialog() {
      this.paymentMethod = "";
      this.paymentAmount = this.remainingBalance;
      this.paymentDialog = true;
    },

    closePaymentDialog() {
      this.paymentDialog = false;
      this.paymentMethod = "";
      this.paymentAmount = 0;
    },

    async confirmPayment() {
      try {
        let res = await this.$store.dispatch("invoices/markInvoiceAsPaid", {
          id: this.invoice._id,
          paymentMethod: this.paymentMethod,
          amount: parseFloat(this.paymentAmount),
        });

        const updatedInvoice = res?.invoice;
        if (updatedInvoice) {
          this.invoice = updatedInvoice;
        } else {
          this.invoice.status = "PAID";
          this.invoice.amountPaid = this.invoice.totalAmount;
        }

        this.$toast.success(res?.message || "Payment recorded");
        this.closePaymentDialog();

        let transaction = res?.transaction;
        if (transaction && this.invoice.status === "PAID") {
          this.$router.push(`/transactions/${transaction._id}/view`);
        }
      } catch (err) {
        console.error(err);
        this.$toast.error("Failed to update status");
      }
    },

    statusColor(status) {
      switch (status) {
        case "PAID":
          return "green";
        case "PARTIALLY_PAID":
          return "blue";
        case "PENDING":
          return "orange";
        case "CANCELLED":
          return "red";
        default:
          return "grey";
      }
    },

    async downloadFile(fileKey) {
      try {
        if (!this.document?.fileKey) {
          this.$toast.error("No file found");
          return;
        }

        let response = await this.$store.dispatch(
          "invoices/downloadInvoiceFile",
          this.document.fileKey,
        );

        triggerBlobDownload(
          response.data,
          fileKey.split("/").pop() || "invoice.pdf",
        );

        this.$toast.success("Downloaded successfully");
      } catch (err) {
        this.$toast.error("Download failed");
      }
    },

    async shareToWhatsApp() {
      try {
        if (!this.document?.fileKey) {
          this.$toast.error("No document available");
          return;
        }

        let fileShared = false;

        if (navigator.share && navigator.canShare) {
          try {
            // Fetch through our own backend proxy, not the raw bucket URL —
            // fetching document.fileUrl directly is a cross-origin request
            // that silently fails whenever the bucket's CORS policy doesn't
            // allow this origin, which was quietly forcing the link
            // fallback below even on devices that fully support file
            // sharing. The proxy is same-origin, so this always works.
            const response = await this.$store.dispatch(
              "invoices/downloadInvoiceFile",
              this.document.fileKey,
            );

            const filename = this.document.fileName || this.document.fileKey.split("/").pop() || "invoice.pdf";
            const file = new File([response.data], filename, {
              type: this.document.fileType || "application/pdf",
            });

            if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                files: [file],
                title: "Invoice",
                text: `Invoice #${this.invoice.invoiceNumber}`,
              });

              fileShared = true;
            }
          } catch (err) {
            console.warn("File share failed, fallback to link", err);
          }
        }

        if (!fileShared) {
          // WhatsApp's send-with-text URL scheme can only ever prefill a
          // text message — it has no way to attach a file, on any device.
          // This opens the user's own WhatsApp (app or Web) so *they* pick
          // any contact from their chat list, rather than us sending to a
          // fixed number — that's the point of this fallback, not a bug.
          //
          // Use the direct bucket URL (fixed at upload time, always a real
          // public HTTPS address), not a URL built from window.location —
          // the sender's own browser origin could be localhost/a dev host/
          // an internal domain unreachable by whoever receives the link.
          const downloadUrl = this.document.fileUrl;

          const text =
            `Invoice #${this.invoice.invoiceNumber}\n` +
            `Here is your invoice PDF, tap to download:\n` +
            `${downloadUrl}`;

          const encoded = encodeURIComponent(text);

          const isMobile = /Android|iPhone|iPad|iPod/i.test(
            navigator.userAgent,
          );

          const url = isMobile
            ? `https://api.whatsapp.com/send?text=${encoded}`
            : `https://web.whatsapp.com/send?text=${encoded}`;

          window.open(url, "_blank");
        }
      } catch (err) {
        console.error(err);
        this.$toast.error("Share failed");
      }
    },
  },
};
</script>

<style scoped>
.inv-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

/* ================= HEADER BAND ================= */
.inv-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
}

.inv-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.inv-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.inv-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.inv-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.inv-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.inv-status-chip {
  letter-spacing: 0.04em;
}

.inv-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.inv-action-btn {
  border: 2px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}

.inv-action-btn--view {
  background: #f59e0b !important;
}
.inv-action-btn--view:hover {
  background: #d97e06 !important;
}

.inv-action-btn--download {
  background: #0a6cc0 !important;
}
.inv-action-btn--download:hover {
  background: #08589e !important;
}

.inv-action-btn--whatsapp {
  background: #25d366 !important;
}
.inv-action-btn--whatsapp:hover {
  background: #1fb958 !important;
}

.inv-pay-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.inv-pay-btn:hover {
  background: rgba(255, 255, 255, 0.26) !important;
}

.inv-pay-btn.v-btn--disabled {
  background: rgba(255, 255, 255, 0.08) !important;
  color: rgba(255, 255, 255, 0.5) !important;
}

.inv-credit-note-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.inv-credit-note-btn:hover {
  background: rgba(255, 255, 255, 0.26) !important;
}

.inv-credit-note-btn.v-btn--disabled {
  background: rgba(255, 255, 255, 0.08) !important;
  color: rgba(255, 255, 255, 0.5) !important;
}

/* ================= PARTY CARDS ================= */
.inv-party-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 18px;
  height: 100%;
}

.inv-party-card--customer {
  background: #f4f9ff;
  border-color: #e4eefb;
}

.inv-party-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 6px;
}

.inv-party-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.inv-party-line {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  display: flex;
  align-items: center;
}

/* ================= META STRIP ================= */
.inv-meta-strip {
  display: flex;
  align-items: center;
  margin: 20px 0;
  padding: 14px 18px;
  border-radius: 0;
  background: #fbfbfd;
  border: 1px solid #eef1f5;
}

.inv-meta-item {
  flex: 1;
}

.inv-meta-divider {
  width: 1px;
  height: 32px;
  background: #e5e9ef;
  margin: 0 20px;
}

.inv-meta-label {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.inv-meta-value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

@media (max-width: 600px) {
  .inv-meta-strip {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .inv-meta-divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }

  .inv-header {
    padding: 18px 16px;
  }

  .pa-6 {
    padding: 16px !important;
  }
}

/* ================= ITEMS TABLE ================= */
.inv-items-wrap {
  border: 1px solid #eef1f5;
  border-radius: 0;
  /* Not "hidden" — that clipped the inner .table-scroll's horizontal
     scroll entirely on narrow screens, making the Tax%/Amount columns
     permanently unreachable instead of just requiring a swipe. */
  overflow: auto;
}

.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.inv-items-table {
  min-width: 560px;
}

.inv-items-table >>> th {
  background: #f8fafc;
  font-weight: 700 !important;
  font-size: 12px !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b !important;
}

.inv-items-table >>> tbody tr:hover {
  background: #f8fafc;
}

/* ================= TOTALS ================= */
.totals {
  margin-top: 20px;
}

.inv-totals-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 18px;
}

.inv-total-value {
  color: #0a5a90;
}

/* ================= PAYMENT HISTORY ================= */
.inv-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}
</style>
